import React, { useState, useEffect } from "react";
import { Typography, Grid2 } from "@mui/material";
import Divider from "@mui/material/Divider";
import AppButton from "../../components/CommonReusableComponent/AppButton/AppButton";
import MUILoader from "../../components/MUILoader/MUILoader";
import MUITableSearch from "../../components/MUITable/MUITableSearch";
// components
import BranchMasterDataTable from "./components/BranchMasterDataTable";
import CreateNewOrUpdateBranchModal from "./components/CreateNewBranchModal";
import DeleteBranchConfirmationModal from "./components/DeleteBranchConfirmationModal";
import { branchMasterColumnsData } from "./components/BranchMaster.config";
import { handleOutSideDataTableSearch } from "../../common/MuiTableFunctions.config";
import { useSnackbar } from "../../contexts/Snackbar.context";
import {
  APP_MESSAGE,
  ERROR_MESSAGE,
  SNACKBAR_VARIANT,
} from "../../config/Messages.config";
import {
  deleteBranch,
  getAllBranches,
} from "../../helpers/api-functions-helper";
import { getLoggedInUserAfterFirebaseInit, getUserRoleAndScreens } from "../../services/authServices/auth.services";
import { getPageTitle } from "../../common/CommonFunctions";
import UserMasterDataTable_V2 from "./components/BranchMasterDataTable_V2";

const BranchMaster = () => {
  const documentTitle = getPageTitle() && getPageTitle();
  document.title = documentTitle ? `${documentTitle} | Zing Auditor` : "Zing Auditor"
  const authUser = JSON.parse(
    localStorage.getItem(process.env.REACT_APP_DEV_AUTH_USER)
  );
  const { showSnackbar } = useSnackbar();
  const { accountId } = getUserRoleAndScreens();
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [tableDataInstance, setTableDataInstance] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [toggleCreateOrUpdateModal, setToggleCreateOrUpdateModal] =
    useState(false);
  const [toggleDeleteModal, setToggleDeleteModal] = useState(false);
  const [isDeleteClicked, setIsDeleteClicked] = useState(false);
  const [modalDataState, setModalDataState] = useState(null);

  // search functionality
  const [searchText, setSearchText] = useState("");

  // Function used to update assigned plant of the current user
  const updateAssignedPlantOfCurrentUser = async () => {
    try {
      const user_details = await getLoggedInUserAfterFirebaseInit(
        authUser?.uid
      );
      const { assignedBranches } = user_details.data;
      const dev_auth_user = {
        ...authUser,
        assignedBranches: assignedBranches,
      };
      localStorage.setItem(
        process.env.REACT_APP_DEV_AUTH_USER,
        JSON.stringify(dev_auth_user)
      );
    } catch (error) {
      console.log("Error: ", error);
    }
  };

  // Fetch all plants
  const fetchAllBranches = () => {
    setIsPageLoading(true);
    getAllBranches(accountId)
      .then((response) => {
        if (response && response.data) {
          const { statusCode, data } = response.data;
          if (statusCode && statusCode === 200) {
            const activePlants = [...data].filter(branch => branch.isActive)
            setTableDataInstance(activePlants);
            setTableData(activePlants);
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          ERROR_MESSAGE.FAILED_TO_FETCH_DATA,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        setIsPageLoading(false);
      });
  };

  // Function used toggle modal on their types
  const handleToggleModal = (modalType) => {
    if (modalType === "Create/Update") {
      setToggleCreateOrUpdateModal((e) => !e);
    } else {
      setToggleDeleteModal((e) => !e);
    }
  };

  const getRowDataOnClickOfAction = (actionName, data) => {
    if (actionName === "Update Branch") {
      setModalDataState(data);
      handleToggleModal("Create/Update");
    } else if (actionName === "Create Branch") {
      handleToggleModal("Create/Update");
    } else {
      setModalDataState(data);
      handleToggleModal("Delete Branch");
    }
  };

  const deleteExistingBranch = () => {
    setIsDeleteClicked(true);
    deleteBranch(modalDataState?.id, accountId)
      .then((res) => {
        if (res && res.data) {
          // console.log(res.data);
          const { statusCode, success } = res.data;
          if (!success && statusCode !== 200) {
            showSnackbar(
              APP_MESSAGE.PLANT_MASTER.DELETE.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
          } else {
            updateAssignedPlantOfCurrentUser()
            showSnackbar(
              APP_MESSAGE.PLANT_MASTER.DELETE.SUCCESS,
              SNACKBAR_VARIANT.SUCCESS
            );
            handleToggleModal("Delete");
            setModalDataState(null);
            fetchAllBranches();
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          APP_MESSAGE.PLANT_MASTER.DELETE.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        setIsDeleteClicked(false);
      });
  };

  useEffect(() => {
    if (navigator.onLine) {
      fetchAllBranches();
    } else {
      showSnackbar(ERROR_MESSAGE.INTERNET_ERROR, SNACKBAR_VARIANT.ERROR)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Outside Table search functionality
  useEffect(() => {
    if (searchText === "") {
      // Reset to original data if search text is cleared
      setTableData(tableDataInstance);
    } else {
      const filteredData_ = handleOutSideDataTableSearch(
        searchText,
        tableDataInstance
      );
      setTableData(filteredData_);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <React.Fragment>
      {toggleCreateOrUpdateModal ? (
        <CreateNewOrUpdateBranchModal
          Open={toggleCreateOrUpdateModal}
          Title={modalDataState ? "Update Existing Branch" : "Create New Branch"}
          DialogData={modalDataState}
          SetDialogData={setModalDataState}
          ToggleDialogBox={handleToggleModal}
          FetchAllBranches={fetchAllBranches}
          AllBranchNames={
            tableDataInstance[0] &&
            tableDataInstance.map((branch) => branch.name.toLowerCase())
          }
          UpdateAssignedPlantOfCurrentUser={updateAssignedPlantOfCurrentUser}
        />
      ) : null}
      {toggleDeleteModal ? (
        <DeleteBranchConfirmationModal
          ModalState={toggleDeleteModal}
          SetModalState={setModalDataState}
          ToggleFunction={handleToggleModal}
          LoaderState={isDeleteClicked}
          OnSubmit={deleteExistingBranch}
        />
      ) : null}
      <div className="page-content">
        {isPageLoading ? (
          <MUILoader />
        ) : (
          <div>
            <Grid2
              container
              className="d-flex align-items-center my-1"
              spacing={2}
            >
              <Grid2 size={{xs: 12, sm: 9, md: 10, lg: 10}} className="py-1">
                <Typography className="inter-medium-500">
                  {documentTitle}
                </Typography>
              </Grid2>

              <Grid2 size={{xs: 12, sm: 3, md: 2, lg: 2}} className="py-1">
                <AppButton OnPress={() => handleToggleModal("Create/Update")}>
                  Create New Branch
                </AppButton>
              </Grid2>
            </Grid2>

            <Divider
              style={{ color: "GrayText", border: "0.02rem solid GrayText" }}
              className="mt-2"
            />

            <Grid2 container className="my-2" spacing={2}>
              <Grid2 size={{xs: 12, sm: 4, md: 4, lg: 4}}>
                <MUITableSearch
                  CancelSearch={() => {
                    setSearchText("");
                  }}
                  HandleSearch={(e) => setSearchText(e.target.value)}
                  SearchText={searchText}
                />
              </Grid2>
            </Grid2>

            <Grid2 container className="my-2">
              <Grid2 size={{xs: 12}} >
                {/* <BranchMasterDataTable
                  Columns={branchMasterColumnsData}
                  RowData={tableData || []}
                  IsOutSideSearch={true}
                  DenseTable={true}
                  GetRowDataOnClickOfEditIcon={(actionName, data) => {
                    getRowDataOnClickOfAction(actionName, data);
                  }}
                  RowsToDisplay={10}
                  OptionsForRowsPerPage={[5, 10, 15, 25, 50]}
                /> */}
                <UserMasterDataTable_V2
                  Rows={tableData || []}
                  GetRowDataOnClickOfAction={getRowDataOnClickOfAction}
                />
              </Grid2>
            </Grid2>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default BranchMaster;
